/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Button, StyledModal, Text, Icon, Heading } from 'sarsaparilla';

import { reportRateLimitEvent, useNoticeShownEvent } from './analytics';
import { announceAndReload, useCountdownAnnouncements } from './announcements';
import { formatCountdown, resolveWaitMs } from './countdown';
import { useCountdown } from './useCountdown';
import { useIsRateLimitNoticeEnabled } from './useIsRateLimitNoticeEnabled';

import styles from './ControlledRateLimitModal.module.scss';

export interface ControlledRateLimitModalProps {
    /** True while the notice should be shown */
    isOpen: boolean;
    /** Called when the user acknowledges the notice, which is its only exit */
    onAcknowledge: () => void;
    /**
     * Milliseconds the server asked us to wait, e.g. from `parseRetryAfterMs`. Null
     * when it did not say, which counts down a one-minute default.
     */
    retryAfterMs?: number | null;
}

/**
 * The rate-limit notice, driven by props.
 *
 * `RateLimitModal` is the wrapper that drives this from module state and is what
 * services should mount. Reach for this variant only when the 429 state a service
 * wants to render from already lives somewhere else, e.g. in redux — both paths
 * are then the same component underneath and cannot drift apart.
 *
 * The notice blocks: there is no close affordance, Escape does nothing, and
 * clicking the overlay does nothing, so the acknowledge button is the only way
 * out. The design system couples Escape to overlay dismissal, so
 * `shouldCloseOnOverlayClick` disables both.
 *
 * `onRequestClose` is wired even though nothing can currently reach it. The
 * design system defaults it to a no-op, which would make the blocking behavior
 * untestable — a test cannot tell "Escape is disabled" apart from "Escape called
 * a function that does nothing". Wired, dropping `shouldCloseOnOverlayClick`
 * fails the suite instead of silently making the notice dismissible.
 *
 * It is an alert dialog rather than a dialog, because it reports a failure the
 * visitor did not ask for: the role makes a screen reader read the notice out when it
 * appears, so nobody is left waiting on a page that silently stopped working. The
 * design system already points the accessible name at the heading and traps focus
 * inside, and `shouldActionsStickInMobile` pins the acknowledge button to the bottom
 * of a phone viewport — a blocking dialog whose only exit is below the fold is a dead
 * end.
 *
 * It always counts the wait down and then recovers the page, offering a manual reload
 * button for a visitor who does not want to wait. A wait the server did not send, or
 * one too short or too long to watch, falls back to a one-minute default rather than
 * dropping the countdown — see `resolveWaitMs`. Acknowledging cancels the countdown
 * and the reload: once the notice is gone the module has no idea what the visitor is
 * doing, and reloading the document out from under someone who deliberately closed
 * the only warning about it is worse than the broken page.
 *
 * It reports what it did — shown, acknowledged, automatically reloaded — from here
 * rather than from each service's integration, which is what makes the numbers
 * comparable across every microfrontend with no work from service teams. See
 * `./analytics`.
 *
 * Everything here answers to the temporary launch kill switch — see
 * `useIsRateLimitNoticeEnabled`, which is also where the note about removing it
 * lives. It is checked here rather than in the wrapper so it covers a service
 * driving this variant from its own state as well.
 */
export function ControlledRateLimitModal({
    isOpen,
    onAcknowledge,
    retryAfterMs = null,
}: ControlledRateLimitModalProps) {
    const isNoticeEnabled = useIsRateLimitNoticeEnabled();
    const isVisible = isOpen && isNoticeEnabled;

    const waitMs = resolveWaitMs(retryAfterMs);

    // Null while the notice is down, which is what makes acknowledgement cancel the
    // countdown and the reload along with it — and what keeps the kill switch from
    // leaving an invisible notice to reload the page
    const activeWaitMs = isVisible ? waitMs : null;
    const remainingMs = useCountdown(activeWaitMs, () => {
        reportRateLimitEvent('auto_reloaded', retryAfterMs);
        announceAndReload();
    });

    useCountdownAnnouncements(remainingMs);
    useNoticeShownEvent(isVisible, retryAfterMs);

    const handleClose = () => {
        reportRateLimitEvent('acknowledged', retryAfterMs);
        onAcknowledge();
    };

    const handleManualReload = () => {
        reportRateLimitEvent('manual_reloaded', retryAfterMs);
        announceAndReload();
    };

    // Remaining is null only while the notice is down; falling back to the resolved
    // wait keeps the display a real value rather than 0:00 on that render
    const displayMs = remainingMs ?? waitMs;

    return (
        <StyledModal
            isOpen={isVisible}
            size="sm"
            role="alertdialog"
            shouldShowCloseButton={false}
            shouldCloseOnOverlayClick={false}
            shouldActionsStickInMobile
            onRequestClose={handleClose}
        >
            <div className="pt-2 px-2 pb-half">
                <Heading
                    appearance="h4"
                    headingLevel={1}
                    className="mb-2"
                    id="modal-heading"
                >
                    Too Many Requests
                </Heading>

                <Text size="lg" color="gray">
                    You’ve made a lot of requests in a short time, so we’ve paused them
                    briefly.
                </Text>

                <Text size="md" className="mt-2">
                    To keep {process.env.SITE_NAME} running smoothly for everyone, we
                    limit how many requests can be made. While paused, pages may not load
                    or work as expected.
                </Text>

                <Text size="md" className="mt-2">
                    Rate limits are triggered by the number of requests you make over a
                    short period of time. We recommend waiting at least 1 minute to allow
                    the limits to reset before reloading the page. You may get this error
                    again if you reload sooner.
                </Text>

                <div className="my-4">
                    <div className={styles.timer}>
                        <Icon iconName="hourglass-half" size="sm" />
                        {`Page will reload automatically in ${formatCountdown(displayMs)}`}
                    </div>
                </div>

                <div className="mt-3">
                    <Button
                        shouldFitContainer
                        appearance="primary"
                        size="lg"
                        onClick={handleManualReload}
                    >
                        Reload Page Now
                    </Button>
                </div>

                <div className="mt-1">
                    <Button shouldFitContainer appearance="subtle" onClick={handleClose}>
                        Continue with Limited Functionality
                    </Button>
                </div>
            </div>
        </StyledModal>
    );
}
